import type { Venue, VenueSearchParams } from '../types';

/**
 * A venue's pause — "don't write again before ..." — read the way the server reads it.
 *
 * Paused means the date is still ahead, compared every time rather than stored: nothing is
 * written when a pause runs out, so an expired one stays as the record of what the venue said
 * and simply stops counting. See docs/VENUE_PAUSE.md.
 */
export function isPaused(venue: Pick<Venue, 'recontact'> | null | undefined, now: Date = new Date()): boolean {
  const after = venue?.recontact?.after;
  return Boolean(after) && new Date(after!).getTime() > now.getTime();
}

/** The date a pause runs to, whether or not it has passed — `undefined` when the venue has none. */
export function recontactDate(venue: Pick<Venue, 'recontact'> | null | undefined): Date | undefined {
  const after = venue?.recontact?.after;
  if (!after) return undefined;
  const date = new Date(after);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * The next first of *month* (1-12) lying ahead of *now* — this year's when it is still to come.
 *
 * The client twin of `_next_month_start` in `app/utils/venue_pause.py`: the server computes the
 * date it stores, and this is only to preview it. Change one, change the other.
 */
export function nextMonthStart(month: number, now: Date = new Date()): Date {
  const candidate = new Date(now.getFullYear(), month - 1, 1);
  return candidate > now ? candidate : new Date(now.getFullYear() + 1, month - 1, 1);
}

/**
 * The params as the API takes them.
 *
 * `paused: 'all'` is a client-side sentinel meaning "no pause filter"; the API's `paused` is a
 * plain boolean, so it has to come off the wire rather than be sent as a string it would reject.
 */
export function toApiParams<T extends VenueSearchParams>(params: T): Omit<T, 'paused'> & { paused?: 'true' | 'false' } {
  const { paused, ...rest } = params;
  return paused === 'all' || paused === undefined ? rest : { ...rest, paused };
}
